import type { PrepareResponse } from '../api'
import { useT } from '../i18n'

interface ManifestModalProps {
  manifest: PrepareResponse
  onConfirm: () => void
  onCancel: () => void
}

export function ManifestModal({ manifest, onConfirm, onCancel }: ManifestModalProps) {
  const t = useT()
  const incomplete = manifest.manifest.filter(m => !m.complete)
  const missingCount = incomplete.reduce((n, m) => n + m.missing_dates.length, 0)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-abyss/70 p-4 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-2xl border border-hull bg-deep p-5 shadow-console">
        <h2 className="text-lg font-semibold text-foam">{t('map.manifest.title')}</h2>
        <p className="mt-1 text-sm text-mist">
          {manifest.needs_download
            ? manifest.needs_recompute
              ? t('map.manifest.needsDownloadAndRecompute', { count: missingCount })
              : t('map.manifest.needsDownload', { count: missingCount })
            : t('map.manifest.ready')}
        </p>

        <div className="mt-3 max-h-56 overflow-y-auto rounded-lg border border-hull">
          <table className="w-full text-sm">
            <thead className="eyebrow bg-trench text-left">
              <tr>
                <th className="px-3 py-2 font-medium">{t('map.manifest.param')}</th>
                <th className="px-3 py-2 font-medium">{t('map.manifest.status')}</th>
              </tr>
            </thead>
            <tbody>
              {manifest.manifest.map(item => (
                <tr key={item.param} className="border-t border-hull">
                  <td className="px-3 py-2 text-foam">{item.param}</td>
                  <td className="px-3 py-2">
                    {item.complete ? (
                      <span className="text-sonar">{t('map.manifest.present')}</span>
                    ) : (
                      <span className="text-buoy">
                        {t('map.manifest.toDownload', { count: item.missing_dates.length })}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
              {manifest.manifest.length === 0 && (
                <tr>
                  <td colSpan={2} className="px-3 py-3 text-center text-mist">
                    {t('map.manifest.noParams')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg px-4 py-2 text-sm text-mist hover:bg-trench hover:text-foam"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="rounded-lg bg-gradient-to-b from-sonar to-sonar-dim px-4 py-2 text-sm font-semibold text-abyss shadow-sonar hover:from-sonar hover:to-sonar"
          >
            {manifest.needs_download ? t('map.manifest.downloadAndShow') : t('map.manifest.show')}
          </button>
        </div>
      </div>
    </div>
  )
}
